import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect } from 'react';
import { View, Text, StyleSheet, Pressable, ScrollView } from 'react-native';
import { AntDesign, MaterialIcons } from '@expo/vector-icons';
import { PanResponder } from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  useAnimatedProps,
  withSpring,
  runOnJS
} from 'react-native-reanimated';
import Svg, { Path, Defs, Marker } from 'react-native-svg';
import * as Animatable from 'react-native-animatable';
import AvatarHandler from '@components/Global/AvatarHandler/AvatarHandler';

// Arrow data interface (matching DraggableUserCard and HierarchyArrowRenderer requirements)
export interface ArrowData {
  id: string;
  path: string;
  color: string;
  angle: number;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  reset?: boolean;
}

interface User {
  _id: string;
  email?: string;
  profile_information?: {
    name?: string;
    surname?: string;
    avatar?: any;
  };
  role?: string;
  department?: string;
  status?: string;
}

interface Department {
  name: string;
  users: User[];
  color: string;
  count: number;
}

interface DraggableDepartmentProps {
  department: Department;
  initialPosition: { x: number; y: number };
  onPositionChange: (departmentName: string, newPosition: { x: number; y: number }) => void;
  onPositionUpdate?: (departmentName: string, newPosition: { x: number; y: number }) => void;
  dragEnabled?: boolean;
  zoomLevel?: number;
  theme: boolean;
  // Arrow functionality props
  showArrows?: boolean;
  onSnap?: (targetId: string, sourceId: string) => void;
  getAllDepartmentPositions?: () => Record<string, { x: number; y: number }>;
  arrowHandler?: (arrowData: ArrowData) => void;
}

export interface DraggableDepartmentHandle {
  getPosition: () => { x: number; y: number };
  setPosition: (position: { x: number; y: number }) => void;
  departmentName: string;
}

const DraggableDepartment = forwardRef<DraggableDepartmentHandle, DraggableDepartmentProps>(
  ({
    department,
    initialPosition,
    onPositionChange,
    onPositionUpdate,
    dragEnabled = true,
    zoomLevel = 1,
    theme,
    showArrows = true,
    onSnap,
    getAllDepartmentPositions,
    arrowHandler
  }, ref) => {
    // Render counter for debugging (only in dev mode)
    const renderCountRef = useRef(0);
    useEffect(() => {
      renderCountRef.current += 1;
      if (__DEV__ && renderCountRef.current > 2) {
        console.log(`🔄 DraggableDepartment re-rendered: ${department.name} - Count: ${renderCountRef.current}`);
      }
    });

    const [isDragging, setIsDragging] = useState(false);
    const [hideArrows, setHideArrows] = useState(false);
    const translationX = useSharedValue(initialPosition.x);
    const translationY = useSharedValue(initialPosition.y);
    const startPosition = useRef({ x: initialPosition.x, y: initialPosition.y });
    const positionRef = useRef({ x: initialPosition.x, y: initialPosition.y });

    // Arrow functionality shared values
    const absoluteX = useSharedValue(0);
    const absoluteY = useSharedValue(0);
    const arrowPath = useSharedValue('M0,0 L0,0');
    const strokeColor = useSharedValue('#3b82f6');
    const arrowAngle = useSharedValue(270); // Track arrow angle for arrowhead
    const snapThreshold = 150;
    const ctx = useSharedValue({
      startX: 0,
      startY: 0,
      startAbsoluteX: 0,
      startAbsoluteY: 0,
      isSnapped: false,
      snappedIndex: -1
    });
    const isArrowRef = useRef(false);
    const lastSnapTargetRef = useRef<string | null>(null); // Store department name we snapped to - persists across gesture events!
    const cardWidth = 400; // Department card width
    const cardHeight = 300; // Department card height

    // Arrow PanResponder (for connection drawing) - attached to main card content
    const arrowPanResponder = PanResponder.create({
      onStartShouldSetPanResponder: () => !isDragging,
      onPanResponderGrant: (event, gestureState) => {
        const { locationX: viewX, locationY: viewY } = event.nativeEvent;

        ctx.value.startX = 0; // Start from center of the card
        ctx.value.startY = 0; // Start from center of the card  
        ctx.value.startAbsoluteX = absoluteX.value;
        ctx.value.startAbsoluteY = absoluteY.value;
        ctx.value.isSnapped = false;
        ctx.value.snappedIndex = -1;

        isArrowRef.current = true;
      },
      onPanResponderMove: (event, gestureState) => {
        const { dx: tX, dy: tY, moveX: ax, moveY: ay } = gestureState;

        // Update absolute values for gesture tracking (NOT the card position)
        absoluteX.value = ax;
        absoluteY.value = ay;

        // DON'T reset snappedIndex here - let the sticky snap logic manage it!
        // ctx.value.snappedIndex = -1; // REMOVED - this was wiping out snap state every frame!

        // Get all department positions for arrow detection
        const allDepartmentPositions = getAllDepartmentPositions ? getAllDepartmentPositions() : {};
        const currentDepartmentPosition = allDepartmentPositions[department.name] || { x: translationX.value, y: translationY.value };

        // Calculate current drag position - accelerate based on zoom
        // When zoomed in (>= 1): 1:1 finger tracking for precision
        // When zoomed out (< 1): Accelerate to compensate for visual scale
        const zoomAcceleration = zoomLevel >= 1
          ? 1  // No acceleration when zoomed in - precise 1:1 tracking
          : (1 / zoomLevel);  // Accelerate when zoomed out for natural feel
        const dragX = ctx.value.startX + (tX * zoomAcceleration);
        const dragY = ctx.value.startY + (tY * zoomAcceleration);

        // Initialize based on current snap state (sticky snap logic)
        let snappedToDepartment = ctx.value.isSnapped;

        // Department card dimensions
        const cardWidth = 280;
        const cardHeight = 400;

        // Calculate current card center in world coordinates
        const currentCardCenterX = currentDepartmentPosition.x + (cardWidth / 2);
        const currentCardCenterY = currentDepartmentPosition.y + (cardHeight / 2);

        // Convert drag position to world coordinates (from card center)
        const worldDragX = currentCardCenterX + dragX;
        const worldDragY = currentCardCenterY + dragY;

        // Iterate over all department positions to check snapping
        // snappedToDepartment is already initialized above based on ctx.value.isSnapped
        let breakFreeDistance = 250; // Distance to break free from snap

        // If already snapped, check if we should break free
        if (ctx.value.isSnapped && ctx.value.snappedIndex !== -1) {
          const snappedPosition = Object.values(allDepartmentPositions)[ctx.value.snappedIndex];
          if (snappedPosition) {
            const snappedCenterX = snappedPosition.x + (cardWidth / 2);
            const snappedCenterY = snappedPosition.y + (cardHeight / 2);
            const distanceToSnapped = Math.sqrt(
              Math.pow(snappedCenterX - worldDragX, 2) + Math.pow(snappedCenterY - worldDragY, 2)
            );

            // Stay snapped if still within break-free distance
            if (distanceToSnapped < breakFreeDistance) {
              snappedToDepartment = true;
              // Maintain current snap
              const relativeTargetX = snappedCenterX - currentCardCenterX;
              const relativeTargetY = snappedCenterY - currentCardCenterY;

              const controlPoint1X = relativeTargetX * 0.3;
              const controlPoint1Y = relativeTargetY * 0.3;
              const controlPoint2X = relativeTargetX * 0.7;
              const controlPoint2Y = relativeTargetY * 0.7;

              const arrowPathValue = `M0,0 C${controlPoint1X},${controlPoint1Y} ${controlPoint2X},${controlPoint2Y} ${relativeTargetX},${relativeTargetY}`;
              arrowPath.value = arrowPathValue;
              strokeColor.value = '#22c55e';

              const angleValue = Math.atan2(relativeTargetY, relativeTargetX) * (180 / Math.PI);
              arrowAngle.value = angleValue;

              if (arrowHandler) {
                runOnJS(arrowHandler)({
                  id: department.name,
                  path: arrowPathValue,
                  color: '#22c55e',
                  angle: angleValue,
                  startX: currentCardCenterX,
                  startY: currentCardCenterY,
                  endX: snappedCenterX,
                  endY: snappedCenterY,
                  reset: false
                });
              }
            } else {
              // Break free - reset snap and clear ref
              ctx.value.isSnapped = false;
              ctx.value.snappedIndex = -1;
              snappedToDepartment = false; // Important: update local variable too!

              // Draw blue arrow when breaking free
              const controlPoint1X = dragX * 0.5;
              const controlPoint1Y = dragY * 0.5;
              const pathValue = `M0,0 C${controlPoint1X},${controlPoint1Y} ${dragX * 0.8},${dragY * 0.8} ${dragX},${dragY}`;
              arrowPath.value = pathValue;
              strokeColor.value = '#3b82f6'; // Blue when broken free

              const angleValue = Math.atan2(dragY, dragX) * (180 / Math.PI);
              arrowAngle.value = angleValue;

              if (arrowHandler) {
                runOnJS(arrowHandler)({
                  id: department.name,
                  path: pathValue,
                  color: '#3b82f6',
                  angle: angleValue,
                  startX: currentCardCenterX,
                  startY: currentCardCenterY,
                  endX: worldDragX,
                  endY: worldDragY,
                  reset: false
                });
              }

              runOnJS(() => {
                lastSnapTargetRef.current = null;
              })();
            }
          }
        }

        // If not currently snapped, check for new snaps
        if (!snappedToDepartment) {
          Object.entries(allDepartmentPositions).forEach(([departmentName, position], index) => {
            if (departmentName === department.name) return; // Skip self
            if (snappedToDepartment) return; // Already snapped to someone

            const targetX = position.x;
            const targetY = position.y;

            // Calculate target card center
            const targetCenterX = targetX + (cardWidth / 2);
            const targetCenterY = targetY + (cardHeight / 2);

            // Calculate distance from drag point to target center
            const distance = Math.sqrt(
              Math.pow(targetCenterX - worldDragX, 2) + Math.pow(targetCenterY - worldDragY, 2)
            );

            if (distance < snapThreshold) {

              // Calculate relative positions for arrow path from current card center to target center
              const relativeTargetX = targetCenterX - currentCardCenterX;
              const relativeTargetY = targetCenterY - currentCardCenterY;

              // Create smooth bezier curve
              const controlPoint1X = relativeTargetX * 0.3;
              const controlPoint1Y = relativeTargetY * 0.3;
              const controlPoint2X = relativeTargetX * 0.7;
              const controlPoint2Y = relativeTargetY * 0.7;

              const arrowPathValue = `M0,0 C${controlPoint1X},${controlPoint1Y} ${controlPoint2X},${controlPoint2Y} ${relativeTargetX},${relativeTargetY}`;
              arrowPath.value = arrowPathValue;
              strokeColor.value = '#22c55e'; // Green when snapped

              // Calculate arrow angle for arrowhead
              const angleValue = Math.atan2(relativeTargetY, relativeTargetX) * (180 / Math.PI);
              arrowAngle.value = angleValue;

              if (arrowHandler) {
                runOnJS(arrowHandler)({
                  id: department.name,
                  path: arrowPathValue,
                  color: '#22c55e',
                  angle: angleValue,
                  startX: currentCardCenterX,
                  startY: currentCardCenterY,
                  endX: targetCenterX,
                  endY: targetCenterY,
                  reset: false
                });
              }

              ctx.value.snappedIndex = index;
              ctx.value.isSnapped = true;
              snappedToDepartment = true;

              // Store the snap target in ref - this persists across gesture events!
              const setSnapRef = (target: string) => {
                lastSnapTargetRef.current = target;
              };
              runOnJS(setSnapRef)(departmentName);
            }
          });
        }

        // Handle unsnapped state - draw arrow following the drag
        if (!snappedToDepartment) {
          const controlPoint1X = dragX * 0.5;
          const controlPoint1Y = dragY * 0.5;

          const pathValue = `M0,0 C${controlPoint1X},${controlPoint1Y} ${dragX * 0.8},${dragY * 0.8} ${dragX},${dragY}`;
          arrowPath.value = pathValue;
          strokeColor.value = '#3b82f6'; // Blue when not snapped

          // Calculate arrow angle for arrowhead
          const angleValue = Math.atan2(dragY, dragX) * (180 / Math.PI);
          arrowAngle.value = angleValue;

          if (arrowHandler) {
            runOnJS(arrowHandler)({
              id: department.name,
              path: pathValue,
              color: '#3b82f6',
              angle: angleValue,
              startX: currentCardCenterX,
              startY: currentCardCenterY,
              endX: worldDragX,
              endY: worldDragY,
              reset: false
            });
          }

          // SAFETY: If we're drawing a free (blue) arrow and ref is still set, clear it!
          // This handles cases where break-free logic didn't run (e.g., quick release)
          if (ctx.value.isSnapped) {
            ctx.value.isSnapped = false;
            ctx.value.snappedIndex = -1;
          }
          runOnJS(() => {
            if (lastSnapTargetRef.current !== null) {
              lastSnapTargetRef.current = null;
            }
          })();
        }
      },
      onPanResponderRelease: () => {
        // Handle release logic on JS thread with access to ref
        const handleRelease = () => {
          const snappedDepartmentId = lastSnapTargetRef.current;

          // Trigger callback if we snapped
          if (snappedDepartmentId && onSnap) {
            onSnap(snappedDepartmentId, department.name);
          }

          // Clear ref after handling
          lastSnapTargetRef.current = null;
        };

        if (arrowHandler) {
          runOnJS(arrowHandler)({
            id: department.name,
            path: 'M0,0 L0,0',
            color: '#3b82f6',
            angle: 270,
            startX: 0,
            startY: 0,
            endX: 0,
            endY: 0,
            reset: true
          });
        }

        runOnJS(handleRelease)();

        strokeColor.value = '#3b82f6';
        arrowPath.value = 'M0,0 L0,0';
        ctx.value.isSnapped = false;
        ctx.value.snappedIndex = -1;
        isArrowRef.current = false;
      },
    });

    // Drag PanResponder (for moving the card) - attached to drag handle only
    const dragPanResponder = PanResponder.create({
      onStartShouldSetPanResponder: () => dragEnabled && !isDragging,
      onPanResponderGrant: (event, gestureState) => {
        if (dragEnabled) {
          runOnJS(setIsDragging)(true);
          startPosition.current = { x: translationX.value, y: translationY.value };
        }
      },
      onPanResponderMove: (event, gestureState) => {
        if (!isDragging || !dragEnabled) return;

        const { dx, dy } = gestureState;
        translationX.value = startPosition.current.x + dx / zoomLevel;
        translationY.value = startPosition.current.y + dy / zoomLevel;

        // Update ref immediately for real-time tracking
        positionRef.current = { x: translationX.value, y: translationY.value };

        if (onPositionUpdate) {
          runOnJS(onPositionUpdate)(department.name, positionRef.current);
        }
      },
      onPanResponderRelease: (event, gestureState) => {
        if (!isDragging || !dragEnabled) return;

        runOnJS(setIsDragging)(false);

        const gridSize = 50;
        const snappedX = Math.round(translationX.value / gridSize) * gridSize;
        const snappedY = Math.round(translationY.value / gridSize) * gridSize;

        translationX.value = withSpring(snappedX);
        translationY.value = withSpring(snappedY);

        // Update ref with final position
        positionRef.current = { x: snappedX, y: snappedY };

        runOnJS(onPositionChange)(department.name, positionRef.current);
      },
    });

    const animatedStyle = useAnimatedStyle(() => {
      return {
        transform: [
          { translateX: translationX.value },
          { translateY: translationY.value },
        ],
      };
    });

    // Animated components for arrow functionality
    const AnimatedPath = Animated.createAnimatedComponent(Path);

    const animatedProps = useAnimatedProps(() => {
      return {
        d: arrowPath.value,
        stroke: strokeColor.value,
      }
    });

    useImperativeHandle(ref, () => ({
      getPosition: () => positionRef.current,
      setPosition: (position: { x: number; y: number }) => {
        translationX.value = position.x;
        translationY.value = position.y;
        positionRef.current = position;
      },
      departmentName: department.name,
    }));

    return (
      <Animated.View style={[
        animatedStyle,
        {
          position: 'absolute',
          zIndex: isDragging ? 10 : 1,
        }
      ]}>
        {/* Drag Handle - ONLY for dragging */}
        <View
          {...dragPanResponder.panHandlers}
          style={{
            position: 'absolute',
            left: -45,
            top: 15,
            zIndex: 15,
            width: 40,
            height: 40,
            backgroundColor: theme ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.2)',
            borderColor: '#3b82f6',
            borderWidth: 1,
            borderRadius: 20,
            justifyContent: 'center',
            alignItems: 'center',
            opacity: isDragging ? 1 : 0.8
          }}
        >
          <MaterialIcons
            name="drag-indicator"
            size={20}
            color={theme ? "#3b82f6" : "#60a5fa"}
          />
        </View>

        {/* Main Department Card - ONLY for arrow drawing */}
        <Animatable.View
          {...arrowPanResponder.panHandlers}
          transition={'maxHeight'}
          style={[
            styles.departmentContainer,
            {
              backgroundColor: theme ? '#ffffff' : '#2d3748',
              borderWidth: 0,
              shadowColor: department.color,
              overflow: 'visible', // Fix bounds issue - allow content to extend beyond
            },
            isDragging && styles.dragging
          ]}
        >
          {/* Department Header */}
          <View style={[styles.departmentHeader, { backgroundColor: department.color }]}>
            <Text style={[styles.departmentName, { userSelect: 'none' }]}>{department.name}</Text>
            <Text style={[styles.departmentCount, { userSelect: 'none' }]}>{department.count}</Text>
          </View>

          {/* Users List */}
          <ScrollView
            style={styles.usersContainer}
            showsVerticalScrollIndicator={false}
            nestedScrollEnabled={true}
            contentContainerStyle={styles.usersContentContainer}
          >
            {(department.users && Array.isArray(department.users) && department.users.length > 0) ? (
              department.users.filter(user => user && user._id).map((user) => {
                const userName = user.profile_information?.name
                  ? `${user.profile_information.name} ${user.profile_information.surname || ''}`.trim()
                  : user.email?.split('@')[0] || 'Unknown';

                const userAvatar = user.profile_information?.avatar?.img;
                const initials = userName.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);

                return (
                  <View key={user._id} style={[
                    styles.userItem,
                    { borderBottomColor: theme ? '#e2e8f0' : '#4a5568' }
                  ]}>
                    <AvatarHandler
                      avatar={userAvatar}
                      avatarImageStyle={styles.userAvatar}
                      fallbackInitials={initials}
                      fallbackStyle={[
                        styles.userAvatarPlaceholder,
                        { backgroundColor: theme ? '#e2e8f0' : '#4a5568' }
                      ]}
                      fallbackTextStyle={[
                        styles.userAvatarText,
                        { color: theme ? '#2d3748' : '#ffffff' }
                      ]}
                    />
                    <View style={styles.userInfo}>
                      <Text style={[
                        styles.userName,
                        { color: theme ? '#2d3748' : '#ffffff', userSelect: 'none' }
                      ]} numberOfLines={1}>
                        {userName}
                      </Text>
                      <Text style={[
                        styles.userRole,
                        { color: theme ? '#718096' : '#a0aec0', userSelect: 'none' }
                      ]} numberOfLines={1}>
                        {user.role || 'Member'}
                      </Text>
                    </View>
                    {user.status === 'active' && (
                      <View style={[styles.statusIndicator, { backgroundColor: '#10b981' }]} />
                    )}
                  </View>
                );
              })
            ) : (
              <View style={styles.noUsersContainer}>
                <Text style={[
                  styles.noUsersText,
                  { color: theme ? '#718096' : '#a0aec0', userSelect: 'none' }
                ]}>
                  No users in this department
                </Text>
              </View>
            )}
          </ScrollView>
        </Animatable.View>

        {/* Arrow Visualization - Positioned at card center */}
        {/* {showArrows && !hideArrows && !isDragging && (
          <Animatable.View
            transition={'top'}
            style={{
              overflow: 'visible',
              position: 'absolute',
              top: 200, // Center of department card (400/2)
              left: 140, // Center of department card (280/2)
              zIndex: -100,
              maxHeight: 1000, // Large enough for arrows
              maxWidth: 1000,
              backgroundColor: 'transparent',
              pointerEvents: 'none',
            }}
          >
            <Svg
              width={1000}
              height={1000}
              style={{
                overflow: "visible",
                position: "absolute",
                left: -500, // Center the SVG
                top: -500
              }}
            >
              <Defs>
                <Marker
                  id={`arrowhead${department.name}`}
                  viewBox='0 0 10 10'
                  refX={8}
                  refY={5}
                  markerWidth={8}
                  markerHeight={8}
                  orient="auto"
                >
                  <Path
                    d={"M0,0 L0,10 L10,5 z"}
                    fill="#3b82f6"
                  />
                </Marker>
              </Defs>
              <AnimatedPath
                animatedProps={animatedProps}
                fill="transparent"
                stroke="#3b82f6"
                strokeWidth={3}
                markerEnd={`url(#arrowhead${department.name})`}
                opacity={0.8}
                transform="translate(500,500)" // Center the paths at origin
              />
            </Svg>
          </Animatable.View>
        )} */}
      </Animated.View>
    );
  }
);

const styles = StyleSheet.create({
  departmentContainer: {
    width: 280,
    height: 400,
    borderRadius: 16,
    borderWidth: 3,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 6,
    overflow: 'visible', // Fix bounds issue - allow content to extend beyond
  },
  departmentHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    minHeight: 60,
  },
  departmentName: {
    fontSize: 18,
    fontWeight: '700',
    color: 'white',
    flex: 1,
    userSelect: 'none',
  },
  departmentCount: {
    fontSize: 14,
    fontWeight: '600',
    color: 'white',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    userSelect: 'none',
  },
  usersContainer: {
    flex: 1,
    padding: 12,
    minHeight: 200, // Ensure minimum height for users list
    maxHeight: 320, // Limit height to prevent overflow
  },
  usersContentContainer: {
    flexGrow: 1,
    paddingBottom: 8,
  },
  noUsersContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 20,
  },
  noUsersText: {
    fontSize: 14,
    textAlign: 'center',
    fontStyle: 'italic',
  },
  userItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
  },
  userAvatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    marginRight: 12,
  },
  userAvatarPlaceholder: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  userAvatarText: {
    fontSize: 12,
    fontWeight: '600',
    userSelect: 'none',
  },
  userInfo: {
    flex: 1,
    marginRight: 8,
  },
  userName: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 2,
    userSelect: 'none',
  },
  userRole: {
    fontSize: 12,
    userSelect: 'none',
  },
  statusIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  dragging: {
    shadowOpacity: 0.3,
    shadowRadius: 12,
    elevation: 12,
  },
});

const DraggableDepartmentMemoized = React.memo(DraggableDepartment, (prevProps, nextProps) => {
  // Return true to SKIP re-render, false to RE-RENDER

  // Different department? Must re-render
  if (prevProps.department.name !== nextProps.department.name) {
    return false;
  }

  // Check if position changed significantly (more than 1px)
  const positionChanged = (
    Math.abs(prevProps.initialPosition.x - nextProps.initialPosition.x) > 1 ||
    Math.abs(prevProps.initialPosition.y - nextProps.initialPosition.y) > 1
  );

  // Skip re-render if nothing meaningful changed
  if (!positionChanged &&
    prevProps.dragEnabled === nextProps.dragEnabled &&
    prevProps.theme === nextProps.theme &&
    prevProps.showArrows === nextProps.showArrows &&
    prevProps.zoomLevel === nextProps.zoomLevel &&
    prevProps.arrowHandler === nextProps.arrowHandler && // Check reference equality
    prevProps.getAllDepartmentPositions === nextProps.getAllDepartmentPositions && // Check reference equality
    prevProps.onSnap === nextProps.onSnap) {
    return true; // Skip re-render - props haven't changed meaningfully
  }

  // Position changed during drag - allow re-render
  if (positionChanged && nextProps.dragEnabled) {
    return false; // Allow re-render for user-initiated drags
  }

  // Position changed but not during drag (e.g., layout adjustment)
  // Skip re-render to prevent pan/zoom causing unnecessary renders
  if (positionChanged && !nextProps.dragEnabled) {
    return true; // Skip re-render - it's just a layout update
  }

  return false; // Default: allow re-render
});

DraggableDepartmentMemoized.displayName = 'DraggableDepartment';

export default DraggableDepartmentMemoized;
